import { Payment } from "@smeetz/order-common";
import dayjs from "dayjs";
import { cloneDeep } from "lodash";
import { dexieDB } from "@/services/dexie-database";
import { IPendingPayment, PaymentSyncMethod } from "@/types/db/payment-sync";
import { LogLevel, LogService } from "@/services/logging";

/**
 * Dexie-backed implementations of the `@smeetz/payment-common` offline hooks
 * (`storePayment` / `retrievePayments` / `deletePayment`). The payment package
 * calls these when a backend payment sync fails and drains them later via
 * `syncPaymentsFromQueue()`. Rows are keyed by `${paymentId}_${method}` so a
 * create (POST) and a later update (PATCH) of the same payment coexist.
 */

function rowId(paymentId: string, method: PaymentSyncMethod): string {
  return `${paymentId}_${method}`;
}

/** Persist a payment locally for retry (client hook: `storePayment`). */
export async function storePaymentToQueue(
  payment: Payment,
  method: PaymentSyncMethod
): Promise<void> {
  if (!payment?.id) {
    LogService.info(
      "Payment sync: attempted to queue a payment without an id",
      { payment },
      LogLevel.Error
    );
    return;
  }
  const record: IPendingPayment = {
    _id: rowId(payment.id, method),
    paymentId: payment.id,
    method,
    payment: cloneDeep(payment),
    creationDateTime: dayjs().toISOString(),
  };
  try {
    await dexieDB.payments.put(record);
  } catch (err) {
    LogService.info(
      `Payment sync: error queuing payment ${payment.id} (${method})`,
      { params: { err } },
      LogLevel.Error
    );
    throw err;
  }
}

/** Retrieve queued payments for a given method (client hook: `retrievePayments`). */
export async function retrievePaymentsFromQueue(
  method: PaymentSyncMethod
): Promise<Payment[]> {
  const rows = await dexieDB.payments.where("method").equals(method).toArray();
  return rows.map((r) => r.payment);
}

/** Delete a queued payment after a successful sync (client hook: `deletePayment`). */
export async function deletePaymentFromQueue(
  paymentId: string,
  method: PaymentSyncMethod
): Promise<void> {
  try {
    await dexieDB.payments.delete(rowId(paymentId, method));
  } catch (err) {
    LogService.info(
      `Payment sync: error deleting queued payment ${paymentId} (${method})`,
      { params: { err } },
      LogLevel.Error
    );
  }
}

/** Number of payments still awaiting sync (used to gate the cashfloat-close drain). */
export async function unsyncedPaymentsCount(): Promise<number> {
  return dexieDB.payments.count();
}
